import { DEFAULT_LANGUAGE } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { extractTimeFromUnified, formatDateISO } from '@avanta/shared/utils/date';
import { useQuery } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { hasFixedPsychologist } from 'pages/AppointmentsReserve/utils';

interface UseFetchTopPsychologistsReturn {
  psychologist: Psychologist | undefined;
  isLoading: boolean;
}

export const useFetchTopPsychologists = (): UseFetchTopPsychologistsReturn => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [patient, backofficeCenterId, specialityBooked, dateTime, flow, contractId, treatmentId] =
    useWatch({
      control,
      name: [
        AppointmentsReservePayload.patient,
        AppointmentsReservePayload.backofficeCenterId,
        AppointmentsReservePayload.specialityBooked,
        AppointmentsReservePayload.dateTime,
        AppointmentsReservePayload.flow,
        AppointmentsReservePayload.contractId,
        AppointmentsReservePayload.psychologistServiceBooked,
      ],
    });

  const patientId = patient?.id ?? '';
  const selectedDate = dateTime ? formatDateISO(dateTime, "yyyy-MM-dd'T'HH:mm:ss") : '';
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const isCalendarQueryEnabled =
    !!specialityBooked &&
    !!backofficeCenterId &&
    !!selectedDate &&
    !!selectedTime &&
    !!patientId &&
    !!contractId &&
    !!treatmentId &&
    !hasFixedPsychologist(flow);

  const {
    data: psychologist,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_TOP_PSYCHOLOGISTS_AVAILABILITY,
      patientId,
      selectedDate,
      selectedTime,
      specialityBooked,
      backofficeCenterId,
      contractId,
      treatmentId,
      DEFAULT_LANGUAGE,
    ],
    queryFn: () =>
      AppointmentService.getTopPsychologistsAvailability({
        startDate: selectedDate,
        days: 0,
        specialityId: Number(specialityBooked),
        centerId: backofficeCenterId,
        patientId,
        spokenLanguage: DEFAULT_LANGUAGE,
        slotHour: selectedTime,
        contractId: Number(contractId),
        treatmentId,
      }),
    select: response => response.data[0]?.psychologist,
    enabled: isCalendarQueryEnabled,
  });

  return {
    psychologist: isCalendarQueryEnabled ? psychologist : undefined,
    isLoading: isLoading || isFetching,
  };
};
